import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

// Extended customer profile interface
export interface CustomerProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  bio?: string;
  avatar?: string;
  whatsappEnabled?: boolean;
  whatsappVerifiedAt?: string;
  address:
    | {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
      }
    | string; // Can be object (for users) or string (for landlords/sellers)
  preferences: {
    propertyTypes: string[];
    priceRange: {
      min: number;
      max: number;
    };
    locations: string[];
    bedrooms: number;
    bathrooms: number;
    squareFeet: {
      min: number;
      max: number;
    };
    features: string[];
  };
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
    priceAlerts: boolean;
    newListings: boolean;
    marketUpdates: boolean;
  };

  // User type specific fields
  region?: string;
  userType?: string;
  userSubType?: string;
  hearAboutUs?: string;

  // === RESIDENTIAL BUYER FIELDS ===
  timelineToMoveIn?: string;
  preApproval?: string;
  movingreason?: string;
  savingclosing?: string;
  propertyType?: string;
  firstBuyer?: string;
  bedrooms?: string[];
  bathrooms?: string[];
  neighborhood?: string;
  parking?: string;
  amenitiesMustHaves?: string[];
  otherRE?: string;
  workingWithAgent?: string;
  areasOfInterest?: string;
  toured?: string;
  decisionMaking?: string;
  agentExpectations?: string;
  buyingProcess?: string;
  financialConcerns?: string;
  nextSteps?: string;
  budget?: string;
  buyerNextContactDate?: string;

  // === COMMERCIAL BUYER FIELDS ===
  // Note: budget, timelineToMoveIn, preApproval, savingclosing are already defined above
  location?: string;
  businessType?: string;
  buyerBusinessType?: string; // Backend field name for commercial buyers
  companySize?: string;
  squareFootageNeeded?: number;
  parkingSpacesNeeded?: number;
  layoutPreference?: string;
  accessibilityRequirements?: string[];
  zoningRequirements?: string[];
  businessAge?: string;
  industryRequirements?: string;
  mustHaveFeatures?: string;
  loadingUnloading?: string;

  // === RESIDENTIAL SELLER FIELDS ===
  saletimeframe?: string;
  desiredsaleprice?: string;
  squarefootage?: string;
  basement?: string;
  floors?: string;
  heatingcooling?: string;
  propertytype?: string;
  occupancystatus?: string;
  acceptablefinancing?: string;
  condition?: string;
  selleramenities?: string[];
  ownership?: string;
  renovations?: string;
  mortgage?: string;
  reasonforsale?: string;
  schedulevisit?: string;
  liens?: string;
  legaldisputes?: string;
  restrictions?: string;

  // === COMMERCIAL SELLER FIELDS ===
  annualRevenue?: number;
  employeeCount?: number;
  leaseTerms?: string;
  tenantInformation?: any;
  tenantCount?: number;
  leaseStartDate?: string; // Used by Commercial Seller
  leaseEndDate?: string;
  monthlyRentPerTenant?: number;
  commercialPropertyType?: string;
  capRate?: number;
  noi?: number;

  // === RESIDENTIAL RENTER FIELDS ===
  affiliatedprograms?: string;
  customAffiliation?: string;
  moveindate?: string;
  leaselength?: string;
  unittype?: string[];
  customunit?: string;
  maxbudget?: string;
  furnishedornot?: string;
  pets?: string;
  laundrypref?: string;
  renteramenities?: string[];
  customamenities?: string;
  dealbreakers?: string;
  buildings?: string;
  reachedout?: string;
  creditscore?: string;
  standardreq?: string;
  guarantor?: string;

  // === UNIFIED CONTACT MANAGEMENT (ALL CUSTOMER TYPES) ===
  contactStatus?: string;
  nextContactDate?: string;

  // === RESIDENTIAL RENTER FIELDS (WITH LEGACY CONTACT INFO) ===
  renterContactStatus?: string;
  renterNextContactDate?: string;

  // === COMMERCIAL RENTER FIELDS ===
  commercialBusinessType?: string;
  commercialParkingRequirements?: number;
  commercialAccessibilityNeeds?: string[];
  interestedCommercialSpaces?: string;
  touredProperties?: string;
  buildOutNeeds?: string;
  canProvideFinancialDocuments?: string;
  canProvideLicense?: string;
  openToPersonalGuaranty?: string;
  // Note: leaseStartDate is already defined in Commercial Seller fields above
  // Note: layoutPreference, businessAge, industryRequirements are already defined in Commercial Buyer fields above
  commercialSquareFootageNeeded?: number;
  hoursOfOperation?: string;
  neighborhoods?: string[];
  // Note: zoningRequirements is already defined in Commercial Buyer fields above (as string[])
  commercialContactStatus?: string;
  commercialNextContactDate?: string;

  // === LANDLORD FIELDS (SHARED) ===
  monthlyRent?: number; // Monthly rent (frontend field name)
  landlordMonthlyRent?: number; // Monthly rent (legacy field name)
  propertyManagement?: string; // Property management (frontend field name)
  landlordPropertyManagement?: string; // Property management (legacy field name)
  landlordMaintenanceRecords?: any[];

  // === RESIDENTIAL LANDLORD SPECIFIC FIELDS ===
  landlordBedrooms?: number; // Number of bedrooms for landlord properties
  landlordBathrooms?: number; // Number of bathrooms for landlord properties
  targetOccupancy?: string; // Target occupancy rate (90+, 80-90, etc.)
  investmentGoal?: string; // Primary investment goal
  tenantScreening?: string; // Tenant screening preference

  // === COMMERCIAL LANDLORD SPECIFIC FIELDS ===
  landlordCommercialPropertyType?: string; // Commercial property type
  squareFootage?: number; // Total square footage
  parkingSpaces?: number; // Number of parking spaces
  tenantType?: string; // Preferred tenant type
  commercialLeaseTerms?: string; // Preferred lease terms
  minimumLeaseLength?: number; // Minimum lease length (months)
  tenantImprovements?: string; // Tenant improvement allowance

  createdAt: string;
  updatedAt: string;
}

interface CustomerProfileState {
  profile: CustomerProfile | null;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: CustomerProfileState = {
  profile: null,
  isLoading: false,
  isUpdating: false,
  error: null,
  lastUpdated: null,
};

// Mock customer profile data
// Mock data for development (kept for reference)
/*
const mockCustomerProfile: CustomerProfile = {
  id: "customer_1",
  firstName: "John",
  lastName: "Doe",
  email: "john.doe@example.com",
  phone: "+1 (555) 123-4567",
  bio: "Looking for my dream home in the city center with modern amenities and great connectivity.",
  avatar: "",
  address: {
    street: "123 Main Street",
    city: "New York",
    state: "NY",
    zipCode: "10001",
    country: "USA",
  },
  preferences: {
    propertyTypes: ["apartment", "condo"],
    priceRange: {
      min: 500000,
      max: 1200000,
    },
    locations: ["Manhattan", "Brooklyn"],
    bedrooms: 2,
    bathrooms: 2,
    squareFeet: {
      min: 1000,
      max: 2000,
    },
    features: ["parking", "gym", "pool", "balcony"],
  },
  notifications: {
    email: true,
    sms: false,
    push: true,
    priceAlerts: true,
    newListings: true,
    marketUpdates: false,
  },
  createdAt: "2024-01-15T10:30:00Z",
  updatedAt: "2024-01-20T14:45:00Z",
};
*/

// Async thunks
export const fetchCustomerProfile = createAsyncThunk(
  "customerProfile/fetchCustomerProfile",
  async (_, { rejectWithValue }) => {
    try {
      // Use the dedicated customer profile API
      const { customerProfileApi } = await import(
        "@/services/api/customerProfile"
      );
      const profile = await customerProfileApi.getProfile();
      return profile;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch customer profile"
      );
    }
  }
);

export const updateCustomerProfile = createAsyncThunk(
  "customerProfile/updateCustomerProfile",
  async (profileData: any, { rejectWithValue }) => {
    try {
      // Use the dedicated customer profile API
      const { customerProfileApi } = await import(
        "@/services/api/customerProfile"
      );
      const updatedProfile = await customerProfileApi.updateProfile(
        profileData
      );
      return updatedProfile;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update customer profile"
      );
    }
  }
);

export const updateCustomerPreferences = createAsyncThunk(
  "customerProfile/updateCustomerPreferences",
  async (
    preferences: Partial<CustomerProfile["preferences"]>,
    { rejectWithValue }
  ) => {
    try {
      const { customerProfileApi } = await import(
        "@/services/api/customerProfile"
      );
      const updatedPreferences = await customerProfileApi.updatePreferences(
        preferences
      );
      return updatedPreferences;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update preferences"
      );
    }
  }
);

export const updateNotificationSettings = createAsyncThunk(
  "customerProfile/updateNotificationSettings",
  async (
    notifications: Partial<CustomerProfile["notifications"]>,
    { rejectWithValue }
  ) => {
    try {
      const { customerProfileApi } = await import(
        "@/services/api/customerProfile"
      );
      const updatedNotifications = await customerProfileApi.updateNotifications(
        notifications
      );
      return updatedNotifications;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update notification settings"
      );
    }
  }
);

export const uploadAvatar = createAsyncThunk(
  "customerProfile/uploadAvatar",
  async (file: File, { rejectWithValue }) => {
    try {
      const { customerProfileApi } = await import(
        "@/services/api/customerProfile"
      );
      const result = await customerProfileApi.uploadAvatar(file);
      return result.avatar;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to upload avatar"
      );
    }
  }
);

const customerProfileSlice = createSlice({
  name: "customerProfile",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearProfile: (state) => {
      state.profile = null;
      state.lastUpdated = null;
    },
    updateProfileField: (
      state,
      action: PayloadAction<Partial<CustomerProfile>>
    ) => {
      if (state.profile) {
        state.profile = { ...state.profile, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch customer profile
      .addCase(fetchCustomerProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload as any; // Cast User to CustomerProfile for now
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchCustomerProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update customer profile
      .addCase(updateCustomerProfile.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateCustomerProfile.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.profile = action.payload as any; // Cast User to CustomerProfile for now
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(updateCustomerProfile.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // Update customer preferences
      .addCase(updateCustomerPreferences.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateCustomerPreferences.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (state.profile && action.payload) {
          state.profile = { ...state.profile, ...action.payload };
          state.profile.updatedAt = new Date().toISOString();
        }
        state.error = null;
      })
      .addCase(updateCustomerPreferences.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // Update notification settings
      .addCase(updateNotificationSettings.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (state.profile && action.payload) {
          state.profile = { ...state.profile, ...action.payload };
          state.profile.updatedAt = new Date().toISOString();
        }
        state.error = null;
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // Upload avatar
      .addCase(uploadAvatar.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(uploadAvatar.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (state.profile) {
          state.profile.avatar = action.payload;
          state.profile.updatedAt = new Date().toISOString();
        }
        state.error = null;
      })
      .addCase(uploadAvatar.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearProfile, updateProfileField } =
  customerProfileSlice.actions;

export default customerProfileSlice.reducer;
